import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {twMerge} from '../../../common/twMerge';
import {TextareaProps} from './TextareaProps.interface';

export function Textarea({
  value: _value = '',
  maxLength = 100,
  onChange,
  applyAutoHeight,
  isMobile,
  className,
  ...textareaProps
}: TextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 초기 height값을 잡아두고, 이를 초기화값으로 사용합니다.
  const [initialHeightPx, setInitialHeightPx] = useState(0);
  useEffect(() => {
    if (textareaRef.current) {
      setInitialHeightPx(textareaRef.current.clientHeight);
    }
  }, []);

  const value = useMemo(() => {
    return String(_value).slice(0, maxLength);
  }, [maxLength, _value]);

  useEffect(() => {
    if (!textareaRef.current || !initialHeightPx) {
      return;
    }
    textareaRef.current.style.height = `${initialHeightPx}px`;
    if (applyAutoHeight) {
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [applyAutoHeight, initialHeightPx, value]);

  const onChangeHandler = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      // e.target.value에는 이번에 입력한 텍스트가 포함되어 들어가는데 이 수치가 maxLength를 넘어선 수치라면 기존 value를 그대로 사용하도록 처리합니다.
      if (e.target.value.length > maxLength) {
        e.target.value = value;
      }
      onChange?.(e);
    },
    [maxLength, onChange, value]
  );
  return (
    <textarea
      ref={textareaRef}
      className={twMerge(
        'font-medium1 w-full resize-none whitespace-pre-wrap break-all bg-transparent text-theme-solid-100 outline-hidden scrollbar-hide placeholder:text-theme-solid-40 disabled:text-theme-solid-40 disabled:placeholder:text-theme-solid-40',
        textareaProps.readOnly &&
          'text-theme-solid-40 placeholder:text-theme-solid-40',
        !isMobile && 'font-small1 h-18px',
        className
      )}
      onChange={onChangeHandler}
      // value가 비어있을 때의 높이를 기본값으로 잡기 위해 잠깐 빈 값을 넣어줍니다.
      value={initialHeightPx ? value : ''}
      {...textareaProps}
    />
  );
}
